// =============================================================================
// LOGIN 2FA FORM COMPONENT
// =============================================================================
// Ubicación: src/components/TwoFactor/Login2FAForm.tsx
// =============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Typography, Space, Alert } from 'antd';
import { message } from '../../services/toastMessage';
import { SafetyOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { login2FA, verifyBackupCode } from '../../services/AuthenticationService';
import { useAuth } from '../../contextProviders/AuthProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Title, Text, Link } = Typography;

interface Login2FAFormProps {
  userId: string;
  email: string;
  onBack: () => void;  // Volver al login normal
}

const Login2FAForm: React.FC<Login2FAFormProps> = ({ userId, email, onBack }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const inputRef = useRef<any>(null);
  const { login } = useAuth();

  // Auto-focus on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [useBackupCode]);

  // ==========================================================================
  // HANDLE TOTP CODE SUBMISSION
  // ==========================================================================
  const handleSubmitCode = async () => {
    if (code.length !== 6) {
      message.warning(translate(t, TRANSLATIONS.LOGIN_2FA_CODE_WARNING));
      return;
    }

    setLoading(true);
    try {
      const response = await login2FA(userId, code);

      // Login successful - store tokens
      login(response.token, response.refreshToken);

      message.success(translate(t, TRANSLATIONS.LOGIN_2FA_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.LOGIN_2FA_INVALID_CODE_ERROR));
      setCode(''); // Clear code on error
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // HANDLE BACKUP CODE SUBMISSION
  // ==========================================================================
  const handleSubmitBackupCode = async () => {
    if (!backupCode.trim()) {
      message.warning(translate(t, TRANSLATIONS.LOGIN_2FA_BACKUP_CODE_WARNING));
      return;
    }

    setLoading(true);
    try {
      const response = await verifyBackupCode(userId, backupCode);

      if (response.success && response.token && response.refreshToken) {
        // Login successful - store tokens
        login(response.token, response.refreshToken);

        message.success({
          content: t(TRANSLATIONS.LOGIN_2FA_BACKUP_SUCCESS, { count: response.remainingBackupCodes }) as string,
          duration: 5
        });
      } else {
        message.error(translate(t, TRANSLATIONS.LOGIN_2FA_BACKUP_INVALID));
      }
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.LOGIN_2FA_BACKUP_INVALID));
      setBackupCode(''); // Clear code on error
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // AUTO-SUBMIT WHEN 6 DIGITS ENTERED
  // ==========================================================================
  useEffect(() => {
    if (code.length === 6 && !loading) {
      handleSubmitCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <div style={{ maxWidth: 400, margin: '50px auto', padding: '0 16px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <SafetyOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          <Title level={3} style={{ marginTop: 16 }}>
            {translate(t, TRANSLATIONS.LOGIN_2FA_TITLE)}
          </Title>
          <Text type="secondary">
            {translate(t, TRANSLATIONS.LOGIN_2FA_LOGGING_IN_AS)} <strong>{email}</strong>
          </Text>
        </div>

        {/* Alert */}
        <Alert
          message={translate(t, useBackupCode ? TRANSLATIONS.LOGIN_2FA_ALERT_TITLE_BACKUP : TRANSLATIONS.LOGIN_2FA_ALERT_TITLE_CODE)}
          description={translate(t, useBackupCode ? TRANSLATIONS.LOGIN_2FA_ALERT_DESCRIPTION_BACKUP : TRANSLATIONS.LOGIN_2FA_ALERT_DESCRIPTION_CODE)}
          type="info"
          showIcon
        />

        {/* Form */}
        {!useBackupCode ? (
          // TOTP Code Input
          <div>
            <Text strong>{translate(t, TRANSLATIONS.LOGIN_2FA_CODE_LABEL)}</Text>
            <Input
              ref={inputRef}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              size="large"
              disabled={loading}
              style={{
                fontSize: 24,
                textAlign: 'center',
                letterSpacing: 8,
                fontFamily: 'monospace',
                marginTop: 8
              }}
            />
            <div style={{ marginTop: 8, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {translate(t, TRANSLATIONS.LOGIN_2FA_CODE_REFRESH_HINT)}
              </Text>
            </div>
          </div>
        ) : (
          // Backup Code Input
          <div>
            <Text strong>{translate(t, TRANSLATIONS.LOGIN_2FA_BACKUP_CODE_LABEL)}</Text>
            <Input
              ref={inputRef}
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              placeholder="ABCD-1234"
              size="large"
              disabled={loading}
              style={{
                fontSize: 18,
                textAlign: 'center',
                letterSpacing: 2,
                fontFamily: 'monospace',
                marginTop: 8
              }}
            />
            <Button
              type="primary"
              block
              size="large"
              onClick={handleSubmitBackupCode}
              loading={loading}
              disabled={!backupCode.trim()}
              style={{ marginTop: 16 }}
            >
              {translate(t, TRANSLATIONS.LOGIN_2FA_VERIFY_BACKUP_BUTTON)}
            </Button>
          </div>
        )}

        {/* Toggle Backup Code */}
        <div style={{ textAlign: 'center' }}>
          <Link
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setCode('');
              setBackupCode('');
            }}
            disabled={loading}
          >
            {translate(t, useBackupCode ? TRANSLATIONS.LOGIN_2FA_USE_AUTHENTICATOR_LINK : TRANSLATIONS.LOGIN_2FA_USE_BACKUP_LINK)}
          </Link>
        </div>

        {/* Back Button */}
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          disabled={loading}
          block
        >
          {translate(t, TRANSLATIONS.LOGIN_2FA_BACK_TO_LOGIN)}
        </Button>

        {/* Help Text */}
        <Alert
          message={translate(t, TRANSLATIONS.LOGIN_2FA_HELP_TITLE)}
          description={
            <div>
              <p>• {translate(t, TRANSLATIONS.LOGIN_2FA_HELP_TIP_1)}</p>
              <p>• {translate(t, TRANSLATIONS.LOGIN_2FA_HELP_TIP_2)}</p>
              <p>• {translate(t, TRANSLATIONS.LOGIN_2FA_HELP_TIP_3)}</p>
            </div>
          }
          type="warning"
          showIcon
          style={{ fontSize: 12 }}
        />
      </Space>
    </div>
  );
};

export default Login2FAForm;
